import React, { useMemo } from 'react'
import {
  ListPoliciesRequest,
  PolicyRecord,
} from '@trustero/trustero-api-web/lib/model/policy_pb'
import Dropdown from 'react-bootstrap/Dropdown'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import styled from 'styled-components/macro'
import { TestIds } from 'src/Utils/testIds'
import { useNewModel } from 'src/components/async/hooks/useNewModel'
import {
  StyledDropdownMenu,
  StyledDropdownToggle,
} from '../../../Reusable/StandardDropdown/StandardDropdown.styles'
import {
  MultiSelectDropdownCheckBox,
  MultiSelectDropdownCheckBoxLabel,
  MultiSelectDropdownItem,
} from '../../../Reusable/MultiSelectDropdown'
import { MenuPlaceholder } from '../../../Placeholders/MenuPlaceholder'
import { ListItemPlaceholder } from '../../../Placeholders/ListItemPlaceholder'
import { useSwrImmutableGrpc } from '../../../async/useSwrImmutableGrpc'
import { boolValue } from '../../../../Utils/primitives'
import { usePolicy } from '../../../async/model'

const StyledPolicyDropdownMenu = styled(StyledDropdownMenu)`
  max-height: 250px;
  overflow-y: auto;
`

export const PolicyDropdown = ({
  selectedItem,
  selectedItems = [],
  multiSelect = false,
  onPolicySelected,
}: {
  selectedItem?: React.ReactNode
  selectedItems?: string[]
  multiSelect?: boolean
  onPolicySelected: (p: PolicyRecord.AsObject) => void
}): JSX.Element => {
  const newModel = useNewModel()
  const request = useMemo(() => {
    const req = new ListPoliciesRequest().setIsDismissed(boolValue(false))
    if (newModel) {
      req.setUseNewModel(newModel)
    }
    return req
  }, [newModel])
  const {
    response: { data: policyIds },
  } = useSwrImmutableGrpc(PolicyPromiseClient.prototype.listIds, request)

  if (!policyIds) {
    return (
      <Dropdown.Menu as={StyledDropdownMenu}>
        <MenuPlaceholder height={96} />
      </Dropdown.Menu>
    )
  }

  return (
    <Dropdown
      id="add-model-form-policy-dropdown"
      data-testid={TestIds.POLICIES_DROPDOWN}
      drop="up"
      autoClose={multiSelect ? 'outside' : true}
    >
      <Dropdown.Toggle as={StyledDropdownToggle} width="100%">
        {selectedItem
          ? selectedItem
          : multiSelect
          ? 'Select policies'
          : 'Select a policy'}
      </Dropdown.Toggle>
      <Dropdown.Menu as={StyledPolicyDropdownMenu}>
        {policyIds
          ?.getItemList()
          .map((policyId: string) => (
            <PolicyDropdownItem
              key={policyId}
              policyId={policyId}
              onPolicySelected={onPolicySelected}
              selectedItems={selectedItems}
              multiSelect={multiSelect}
            />
          )) ?? <MenuPlaceholder height={96} />}
      </Dropdown.Menu>
    </Dropdown>
  )
}

const PolicyDropdownItem = ({
  policyId,
  onPolicySelected,
  selectedItems = [],
  multiSelect = false,
}: {
  policyId: string
  onPolicySelected: (p: PolicyRecord.AsObject) => void
  selectedItems?: string[]
  multiSelect?: boolean
}): JSX.Element => {
  const { data: policy } = usePolicy(policyId)

  if (!policy) {
    return <ListItemPlaceholder height={32} />
  }
  const isSelected = [policy.getModelId(), policyId, policy.getId()].some(
    (id) => !!id && selectedItems.includes(id),
  )
  const normalizedPolicy = {
    ...policy.toObject(),
    id: policy.getId(),
    modelId: policy.getModelId(),
    name: policy.getName(),
  }

  if (multiSelect) {
    return (
      <Dropdown.Item
        id={policy.getId()}
        eventKey={policy.getId()}
        as={MultiSelectDropdownItem}
      >
        <MultiSelectDropdownCheckBoxLabel>
          <MultiSelectDropdownCheckBox
            id={policy.getId()}
            name={policy.getId()}
            checked={isSelected}
            onChange={() => onPolicySelected(normalizedPolicy)}
          />
          {policy.getName()}
        </MultiSelectDropdownCheckBoxLabel>
      </Dropdown.Item>
    )
  }

  return (
    <Dropdown.Item
      id={policy.getId()}
      eventKey={policy.getId()}
      onClick={() => onPolicySelected(normalizedPolicy)}
    >
      {policy.getName()}
    </Dropdown.Item>
  )
}
